import React from "react";
import { TrendingUp, TrendingDown, Plus } from "lucide-react";
import SignalDot from "./SignalDot";
import RowFreshness from "./RowFreshness";

function fmt(n, d = 2) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function ScoreMeter({ score, direction }) {
  const isBull = direction === "bullish";
  const color = isBull ? "var(--bull)" : "var(--bear)";
  const glow = isBull ? "var(--bull-glow)" : "var(--bear-glow)";
  return (
    <div className="flex items-center gap-2 min-w-[88px]">
      <div className="flex-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
        <div style={{ width: `${score}%`, background: color, boxShadow: `0 0 12px ${glow}` }} className="h-full rounded-full transition-all duration-500" />
      </div>
      <span className="font-mono text-[11px] text-white tabular-nums w-6 text-right">{score}</span>
    </div>
  );
}

export default function ResultsTable({ results, onSelect, selectedTicker, onAddWatchlist, onHover }) {
  if (!results || results.length === 0) {
    return (
      <div className="card-surface p-12 text-center" data-testid="results-empty">
        <div className="label-mono mb-2">No matches</div>
        <p className="text-white/50 text-sm">Adjust your filters and run the scan again.</p>
      </div>
    );
  }
  return (
    <div className="card-surface overflow-hidden fade-in" data-testid="results-table">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div>
          <div className="label-mono">Scan Results</div>
          <div className="text-white text-[17px] font-semibold tracking-tight mt-0.5">
            {results.length} <span className="text-white/40 text-sm font-normal">matches</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[var(--bull)] pulse-dot"></span>
          <span className="label-mono text-white">Live</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="label-mono">
              <th className="px-5 py-2.5"></th>
              <th className="px-2 py-2.5">Ticker</th>
              <th className="px-2 py-2.5 text-right">Price</th>
              <th className="px-2 py-2.5 text-right">Chg %</th>
              <th className="px-2 py-2.5 text-right">RSI</th>
              <th className="px-2 py-2.5 text-right">MACD</th>
              <th className="px-2 py-2.5 text-right">VWAP Δ</th>
              <th className="px-2 py-2.5 text-right">BB</th>
              <th className="px-2 py-2.5">RVOL</th>
              <th className="px-2 py-2.5 text-right">EMA9</th>
              <th className="px-2 py-2.5">TF</th>
              <th className="px-2 py-2.5">Score</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => {
              const isSel = r.ticker === selectedTicker;
              return (
                <tr key={r.ticker + i} data-testid={`result-row-${r.ticker}`}
                  onClick={() => onSelect && onSelect(r.ticker)}
                  onMouseEnter={(e) => onHover && onHover(r.ticker, e.currentTarget.getBoundingClientRect())}
                  onMouseLeave={() => onHover && onHover(null, null)}
                  className={`cursor-pointer transition-colors border-t border-white/5 ${isSel ? "bg-white/[0.04]" : "hover:bg-white/[0.03]"} fade-in`}
                  style={{ animationDelay: `${Math.min(i * 18, 360)}ms` }}>
                  <td className="px-3 py-2">
                    <SignalDot variant={r.direction === "bullish" ? "bull" : "bear"} strength={r.strength} size={8} />
                  </td>
                  <td className="px-2 py-2.5">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold text-[13px] tracking-tight">{r.ticker}</span>
                        {r.direction === "bullish"
                          ? <TrendingUp className="w-3 h-3 text-[var(--bull)]" />
                          : <TrendingDown className="w-3 h-3 text-[var(--bear)]" />}
                        <span className="hidden lg:inline text-white/35 text-xs truncate max-w-[120px]">{r.name}</span>
                      </div>
                      <RowFreshness status={r.status} ageSec={r.age_sec} />
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-white text-[13px] tabular-nums">{fmt(r.price)}</td>
                  <td className={`px-2 py-2.5 text-right font-mono text-[13px] tabular-nums ${r.change_pct >= 0 ? "text-bull" : "text-bear"}`}>
                    {r.change_pct >= 0 ? "+" : ""}{fmt(r.change_pct, 2)}%
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-white/80 text-[12px] tabular-nums">{fmt(r.indicators.rsi, 1)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-white/80 text-[12px] tabular-nums">{fmt(r.indicators.macd, 3)}</td>
                  <td className={`px-2 py-2.5 text-right font-mono text-[12px] tabular-nums ${r.indicators.vwap_distance_pct >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"}`}>
                    {r.indicators.vwap_distance_pct >= 0 ? "+" : ""}{fmt(r.indicators.vwap_distance_pct, 2)}
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-white/80 text-[12px] tabular-nums">{fmt(r.indicators.bb_position * 100, 0)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-white/80 text-[12px] tabular-nums">{fmt(r.indicators.rvol, 2)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-white/80 text-[12px] tabular-nums">{fmt(r.indicators.ema9, 2)}</td>
                  <td className="px-2 py-2.5 font-mono text-white/40 text-[11px] uppercase">{r.timeframe}</td>
                  <td className="px-2 py-2.5"><ScoreMeter score={r.score} direction={r.direction} /></td>
                  <td className="px-3 py-2.5">
                    <button onClick={(e) => { e.stopPropagation(); onAddWatchlist && onAddWatchlist(r); }}
                      className="p-1.5 rounded-lg border border-white/8 text-white/50 hover:text-white hover:bg-white/[0.06] hover:border-white/20 transition-colors"
                      title="Add to watchlist" data-testid={`add-watch-${r.ticker}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
